import { FormControl, Popper, TextField } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useEffect, useState } from "react";

import api from "../../../services/api";

import { palette } from "../../../theme/palette";
import { SEARCH_ADDRESSES_BY_PHRASE } from "../../../consts/apis";
import { MANUAL } from "../../../consts/strings";

import { DriverProfileAutocompleteMenuItem } from "./DriverProfileAutocompleteMenuItem";

const useStyles = makeStyles({
  dropdownPopper: {
    zIndex: 1300,
    borderRadius: "0.5rem",
    boxShadow: `0px 3px 4px ${palette.typography.black.high}`,
  },
});

const lastInputValue = {
  start: "",
  end: "",
};

export const DriverProfileAddressAutocomplete = ({
  name,
  error,
  helperText,
  onAddressChange,
  driverProfileEdit,
  disabled,
  displayItemsCount = 10,
  locationType = "start",
}) => {
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [highlightedItemIndex, setHighlightedItemIndex] = useState(-1);
  const [scrollOffsetIndex, setScrollOffsetIndex] = useState(0);
  const [touchState, setTouchState] = useState({});
  const [inputValue, setInputValue] = useState(null);
  const [isDropdownOpen, setIsDropDownOpen] = useState(false);

  const isManual =
    locationType === "end"
      ? driverProfileEdit.endIsManual
      : driverProfileEdit.startIsManual;

  lastInputValue[locationType] = inputValue;

  useEffect(() => {
    if (!isDropdownOpen) return;
    setInputValue("");
    setAddresses([]);
    setHighlightedItemIndex(-1);
    setScrollOffsetIndex(0);
  }, [isDropdownOpen]);

  useEffect(() => {
    (async () => {
      setHighlightedItemIndex(-1);
      setScrollOffsetIndex(0);

      if (inputValue?.length) {
        const {
          data: { addresses },
        } = await api.get(SEARCH_ADDRESSES_BY_PHRASE(inputValue));
        inputValue === lastInputValue[locationType] && setAddresses(addresses);
      } else if (!inputValue?.length) setAddresses([]);
    })();
  }, [inputValue, locationType]);

  const getDropdownItems = () => {
    if (!addresses.length && inputValue)
      return [{ id: MANUAL, fullAddress: MANUAL }];
    else
      return addresses.slice(
        scrollOffsetIndex,
        displayItemsCount + scrollOffsetIndex
      );
  };

  const updatedToHighlightedAddress = () => {
    const items = getDropdownItems();
    const address = items[highlightedItemIndex - scrollOffsetIndex];
    address?.fullAddress !== MANUAL && setInputValue("");
    address && onAddressChange({ address, locationType });
  };

  return (
    <FormControl
      variant={"outlined"}
      labelId={name}
      onKeyDown={({ code }) => {
        if (code === "ArrowDown" && highlightedItemIndex < addresses.length) {
          const newHighlightedItemIndex = highlightedItemIndex + 1;
          if (newHighlightedItemIndex >= scrollOffsetIndex + displayItemsCount)
            setScrollOffsetIndex(scrollOffsetIndex + 1);
          setHighlightedItemIndex(newHighlightedItemIndex);
        } else if (code === "ArrowUp" && highlightedItemIndex > 0) {
          const newHighlightedItemIndex = highlightedItemIndex - 1;
          if (newHighlightedItemIndex < scrollOffsetIndex)
            setScrollOffsetIndex(scrollOffsetIndex - 1);
          setHighlightedItemIndex(newHighlightedItemIndex);
        } else if (code === "Enter" && highlightedItemIndex > -1) {
          updatedToHighlightedAddress();
          setIsDropDownOpen(false);
        }
      }}
      onFocus={() => setIsDropDownOpen(true)}
      onClick={() => setIsDropDownOpen(true)}
      onBlur={() => {
        highlightedItemIndex > -1 && updatedToHighlightedAddress();
        setIsDropDownOpen(false);
      }}
    >
      <TextField
        ref={setAnchorEl}
        variant={"outlined"}
        label={"Search Postcode"}
        labelId={name}
        name={name}
        value={!isManual || isDropdownOpen ? inputValue : MANUAL}
        error={error}
        helperText={helperText}
        FormHelperTextProps={{
          style: { marginLeft: "0px", marginBottom: "-0.5rem" },
        }}
        onChange={({ target }) => setInputValue(target.value)}
        disabled={disabled}
      />
      <Popper
        open={isDropdownOpen}
        className={styles.dropdownPopper}
        anchorEl={anchorEl}
        // Background color has to be specified in style directly or it doesn't work
        style={{ backgroundColor: palette.white }}
        onMouseLeave={() => setHighlightedItemIndex(-1)}
        onWheel={({ deltaY }) => {
          const yPositive = deltaY > 0;
          if (
            !yPositive
              ? scrollOffsetIndex === 0
              : scrollOffsetIndex >= addresses.length - displayItemsCount
          )
            return;
          const newScrollOffsetIndex = scrollOffsetIndex + (yPositive ? 1 : -1);
          setScrollOffsetIndex(newScrollOffsetIndex);
        }}
        onTouchStart={({ touches: [{ clientY }] }) => {
          setTouchState({
            startY: clientY,
            startOffset: scrollOffsetIndex,
            lastTouchIndex: null,
          });
        }}
        onTouchMove={({ touches: [{ clientY }] }) => {
          const { startY, startOffset } = touchState;

          let indexDifference = 0;
          if (startY > clientY)
            indexDifference = Math.floor((startY - clientY) / 25);
          else if (startY < clientY)
            indexDifference = -Math.floor((clientY - startY) / 25);

          setTouchState({
            startY,
            startOffset: scrollOffsetIndex,
            lastTouchIndex: startOffset + indexDifference,
          });
        }}
        onTouchEnd={() => {
          const { lastTouchIndex } = touchState;

          let newScrollOffsetIndex = null;
          if (lastTouchIndex < 0) newScrollOffsetIndex = 0;
          else if (lastTouchIndex >= addresses.length - displayItemsCount)
            newScrollOffsetIndex = addresses.length - displayItemsCount;
          else newScrollOffsetIndex = lastTouchIndex;

          newScrollOffsetIndex !== null &&
            setScrollOffsetIndex(newScrollOffsetIndex);
          setTouchState({
            startY: null,
            startOffset: null,
            lastTouchIndex: null,
          });
        }}
      >
        {getDropdownItems().map((address, index) => {
          const addressIndex = index + scrollOffsetIndex;
          const isHighlighted = addressIndex === highlightedItemIndex;
          const onMouseEnter = () => setHighlightedItemIndex(addressIndex);

          return (
            <DriverProfileAutocompleteMenuItem
              isHighlighted={isHighlighted}
              onMouseEnter={onMouseEnter}
              address={address}
            />
          );
        })}
      </Popper>
    </FormControl>
  );
};
